import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { Separator } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

function ItemGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('group/item-group flex flex-col', className)}
      data-slot="item-group"
      role="list"
      {...props}
    data-sentry-component="ItemGroup" data-sentry-source-file="item.tsx" />
  )
}

function ItemSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn('my-0', className)}
      data-slot="item-separator"
      orientation="horizontal"
      {...props}
    data-sentry-element="Separator" data-sentry-component="ItemSeparator" data-sentry-source-file="item.tsx" />
  )
}

const itemVariants = cva(
  'group/item flex flex-wrap items-center rounded-md border border-transparent text-sm transition-colors duration-100 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [a]:transition-colors [a]:hover:bg-accent/50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'gap-4 p-4',
        sm: 'gap-2.5 px-4 py-3',
      },
      variant: {
        danger: 'border-destructive text-destructive',
        default: 'bg-transparent',
        destructive: 'border-destructive/20 bg-destructive/10 text-destructive',
        muted: 'bg-muted/50',
        outline: 'border-border',
      },
    },
  },
)

function Item({
  className,
  render,
  size = 'default',
  variant = 'default',
  ...props
}: useRender.ComponentProps<'div'> & VariantProps<typeof itemVariants>) {
  return useRender({
    defaultTagName: 'div',
    props: mergeProps<'div'>({ className: cn(itemVariants({ className, size, variant })) }, props),
    render,
    state: {
      size,
      slot: 'item',
      variant,
    },
  })
}

const itemMediaVariants = cva('flex shrink-0 gap-2 [&_svg]:pointer-events-none', {
  defaultVariants: {
    alignment: 'default',
    variant: 'default',
  },
  variants: {
    alignment: {
      center: 'items-center justify-center',
      default:
        'items-center justify-center group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start',
    },
    variant: {
      default: 'bg-transparent',
      icon: "size-8 rounded-sm border bg-muted [&_svg:not([class*='size-'])]:size-4",
      image: 'size-10 overflow-hidden rounded-sm [&_img]:size-full [&_img]:object-cover',
    },
  },
})

function ItemActions({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('flex items-center gap-2', className)} data-slot="item-actions" {...props} data-sentry-component="ItemActions" data-sentry-source-file="item.tsx" />
  )
}

function ItemContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none', className)}
      data-slot="item-content"
      {...props}
    data-sentry-component="ItemContent" data-sentry-source-file="item.tsx" />
  )
}

function ItemDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'line-clamp-3 text-sm leading-normal font-normal text-balance text-muted-foreground',
        '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
        className,
      )}
      data-slot="item-description"
      {...props}
    data-sentry-component="ItemDescription" data-sentry-source-file="item.tsx" />
  )
}

function ItemFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      data-slot="item-footer"
      {...props}
    data-sentry-component="ItemFooter" data-sentry-source-file="item.tsx" />
  )
}

function ItemHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      data-slot="item-header"
      {...props}
    data-sentry-component="ItemHeader" data-sentry-source-file="item.tsx" />
  )
}

function ItemMedia({
  alignment,
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemMediaVariants>) {
  return (
    <div
      className={cn(itemMediaVariants({ alignment, className, variant }))}
      data-slot="item-media"
      data-variant={variant}
      {...props}
    data-sentry-component="ItemMedia" data-sentry-source-file="item.tsx" />
  )
}

function ItemTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 text-sm leading-snug font-medium [&:not(:has([data-slot=skeleton]))]:w-fit',
        className,
      )}
      data-slot="item-title"
      {...props}
    data-sentry-component="ItemTitle" data-sentry-source-file="item.tsx" />
  )
}

export {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
}
